/**
 * handoutFiles - how a FILE becomes a handout in a session, as plain
 * functions: the table's drawer (useHandouts) and the Assets page's session
 * view (useSessionCopies, which is not the table and has no drawer) post
 * the same way (v11.82.0).
 *
 * The order (useHandouts' header says why): the kind, then the downscale,
 * then the size and the session's quota judged on what will UPLOAD, then the
 * DRAFT document (both backends refuse a file on a document that does not
 * exist), then the file and its thumbnail through the files port, then the
 * refs and sizes merged in. A refusal writes nothing.
 */
import { downscaleImage, makeThumb } from '../utils/imageDownscale';
import {
  MAX_EDGE_PX,
  THUMB_EDGE_PX,
  emptyHandout,
  kindOf,
  kindRefusal,
  quotaRefusal,
  sizeRefusal,
  toDoc,
  wouldExceed,
  type Handout,
  type Refused,
} from '../engine/handouts';
import { extensionOf } from '../engine/assets/fileKinds';
import type { Origin } from '../engine/assets/types';
import type { CollectionRead } from '../backend/types';

export interface HandoutsMp {
  onHandoutsUpdate: (
    sessionId: string,
    uid: string | null,
    cb: (read: CollectionRead<Record<string, unknown>>) => void,
  ) => () => void;
  setHandout: (sessionId: string, id: string, doc: Record<string, unknown>, merge?: boolean) => Promise<void>;
  deleteHandout: (sessionId: string, id: string) => Promise<void>;
  uploadHandoutFile: (
    sessionId: string,
    id: string,
    field: 'file' | 'thumb',
    blob: Blob,
    opts: { name: string; contentType: string },
  ) => Promise<string>;
  deleteHandoutFile: (sessionId: string, id: string, field: 'file' | 'thumb', ref: string) => Promise<void>;
}

export interface PostMeta {
  title?: string;
  folder?: string;
  tags?: string[];
  /** The library asset and version the file came from; none for one picked from the computer. */
  origin?: Origin | null;
}

/** Where a file goes and what makes it: the session, who posts, the clock, and the image pipeline. */
export interface FilePoster {
  mp: HandoutsMp;
  sessionId: string;
  me: string;
  now: () => number;
  /** Injectable for tests (jsdom has no canvas). */
  scale?: typeof downscaleImage;
  thumb?: typeof makeThumb;
}

/** One file's outcome: posted, with the bytes it charged the session, or refused. */
export type Posted = { id: string; bytes: number } | { refused: Refused };

export function metaOf(meta: PostMeta): Pick<Handout, 'title' | 'folder' | 'tags'> {
  return {
    title: (meta.title ?? '').trim(),
    folder: (meta.folder ?? '').trim(),
    tags: [...new Set((meta.tags ?? []).map((t) => t.trim()).filter(Boolean))],
  };
}

/** The stored name follows the bytes: a PNG re-encoded as WebP is `x.webp`. */
export function fileName(original: string, type: string): string {
  const ext = extensionOf(type);
  const base =
    original
      .replace(/\.[^.]+$/, '')
      .replace(/[^\w.-]+/g, '_')
      .slice(0, 80) || 'file';
  return ext ? `${base}.${ext}` : original;
}

/**
 * Why a prepared file is refused, or null: the bytes it uploads at against
 * the per-file limit, then the session's quota - charged for what this
 * batch has already posted (`charged`), since the watch has not delivered
 * those yet.
 */
function sizeOrQuotaRefusal(name: string, bytes: number, handouts: readonly Handout[], charged: number) {
  return sizeRefusal(name, bytes) ?? (wouldExceed(handouts, charged + bytes) ? quotaRefusal(name) : null);
}

interface Prepared {
  blob: Blob;
  width: number;
  height: number;
  thumbBlob: Blob | null;
}

/**
 * An image is downscaled and thumbnailed; anything else goes up untouched.
 * A GIF, or an image the runtime cannot re-encode, keeps its bytes - so
 * the prepared size, never the original, is what the limits judge.
 */
async function prepareFile(file: File, kind: string, p: FilePoster): Promise<Prepared> {
  if (kind !== 'image') return { blob: file, width: 0, height: 0, thumbBlob: null };
  const scaled = await (p.scale ?? downscaleImage)(file, { maxEdge: MAX_EDGE_PX });
  const thumbBlob = await (p.thumb ?? makeThumb)(file, THUMB_EDGE_PX);
  return { blob: scaled.blob, width: scaled.width, height: scaled.height, thumbBlob };
}

/** What a prepared file uploads at, thumbnail included - exactly what `size` records. */
const preparedBytes = (prep: Prepared) => prep.blob.size + (prep.thumbBlob?.size ?? 0);

/** Upload the file, then its thumbnail, then merge the refs and sizes (and anything in `also`) in. */
async function uploadAndRecord(p: FilePoster, id: string, file: File, prep: Prepared, also = {}) {
  const { blob, thumbBlob } = prep;
  const file0 = await p.mp.uploadHandoutFile(p.sessionId, id, 'file', blob, {
    name: fileName(file.name, blob.type),
    contentType: blob.type || file.type,
  });
  const jpeg = { name: 'thumb.jpg', contentType: 'image/jpeg' };
  const thumb = thumbBlob ? await p.mp.uploadHandoutFile(p.sessionId, id, 'thumb', thumbBlob, jpeg) : '';
  const refs = { file: file0, thumb, size: preparedBytes(prep), width: prep.width, height: prep.height };
  await p.mp.setHandout(p.sessionId, id, { ...refs, ...also, updatedAt: p.now() }, true);
}

/** One file to post: its new id, what it is called, and the session's handouts with what this batch has charged. */
export interface FilePost {
  id: string;
  file: File;
  meta: PostMeta;
  handouts: readonly Handout[];
  charged: number;
}

/** Post a file as a new handout: refused (nothing written) or posted; a failure after the draft rejects. */
export async function postHandoutFile(p: FilePoster, { id, file, meta, handouts, charged }: FilePost): Promise<Posted> {
  const kind = kindOf(file);
  if (!kind) return { refused: kindRefusal(file)! };
  const prep = await prepareFile(file, kind, p);
  const bytes = preparedBytes(prep);
  const why = sizeOrQuotaRefusal(file.name, bytes, handouts, charged);
  if (why) return { refused: why };
  const draft = { ...emptyHandout(id, kind, p.me, p.now()), ...metaOf(meta), origin: meta.origin ?? null };
  await p.mp.setHandout(p.sessionId, id, toDoc({ ...draft, title: meta.title?.trim() || file.name }));
  await uploadAndRecord(p, id, file, prep);
  return { id, bytes };
}

/**
 * A file handout's bytes replaced IN PLACE by a newer library version: its
 * title, audience and reveal stay. The old file and thumbnail go first, so
 * nothing is left behind on a server that names files apart (Storage).
 * Throws the refusal's sentence when the new bytes will not fit.
 */
export async function replaceHandoutFile(
  p: FilePoster,
  current: Handout,
  file: File,
  origin: Origin,
  handouts: readonly Handout[],
): Promise<void> {
  const kind = kindOf(file);
  if (!kind) throw new Error(kindRefusal(file)!.sentence);
  const prep = await prepareFile(file, kind, p);
  const others = handouts.filter((h) => h.id !== current.id);
  const why = sizeOrQuotaRefusal(current.title, preparedBytes(prep), others, 0);
  if (why) throw new Error(why.sentence);
  if (current.file) await p.mp.deleteHandoutFile(p.sessionId, current.id, 'file', current.file);
  if (current.thumb) await p.mp.deleteHandoutFile(p.sessionId, current.id, 'thumb', current.thumb);
  await uploadAndRecord(p, current.id, file, prep, { kind, origin: JSON.stringify(origin) });
}
